import { useEffect, useRef } from 'react';

export function useAutoLogout({
  timeoutMinutes = 40,
  onLogout,
  disabled = false,
}: {
  timeoutMinutes?: number;
  onLogout: () => void;
  disabled?: boolean;
}) {
  const logoutTimerRef = useRef<number | null>(null);
  const INACTIVITY_LIMIT_MS = timeoutMinutes * 60 * 1000;

  const lastWriteRef = useRef(0);
  const WRITE_THROTTLE_MS = 10_000;

  const updateLastActivity = () => {
    const now = Date.now();
    if (now - lastWriteRef.current > WRITE_THROTTLE_MS) {
      localStorage.setItem('lastActivity', now.toString());
      lastWriteRef.current = now;
    }
  };

  const resetInactivityTimers = () => {
    updateLastActivity();
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    logoutTimerRef.current = window.setTimeout(() => {
      console.warn('User is inactive, logging out session.');
      onLogout();
    }, INACTIVITY_LIMIT_MS);
  };

  const handleUserActivity = () => {
    resetInactivityTimers();
  };

  const handleVisibility = () => {
    if (document.visibilityState === 'visible') {
      const last = parseInt(localStorage.getItem('lastActivity') || '0', 10);
      const now = Date.now();
      if (now - last > INACTIVITY_LIMIT_MS) {
        console.warn('User was away too long, logging out.');
        onLogout();
      } else {
        resetInactivityTimers();
      }
    }
  };

  useEffect(() => {
    if (disabled) return undefined;

    const activityEvents = ['mousemove', 'keydown', 'scroll', 'click'];
    activityEvents.forEach((event) =>
      window.addEventListener(event, handleUserActivity)
    );
    document.addEventListener('visibilitychange', handleVisibility);

    resetInactivityTimers();

    return () => {
      if (logoutTimerRef.current) {
        clearTimeout(logoutTimerRef.current);
        logoutTimerRef.current = null;
      }
      activityEvents.forEach((event) =>
        window.removeEventListener(event, handleUserActivity)
      );
      document.removeEventListener('visibilitychange', handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- handlers are recreated every render; adding them would re-register listeners and reset the timer on each render
  }, [onLogout, disabled]);
}
